import * as THREE from 'three';
import { LocalCoordinates } from '@/types/course.types';

/**
 * Create a parabolic arc curve between two points
 * @param start Starting local coordinates
 * @param end Ending local coordinates
 * @param apexHeight Height of the arc at its peak (meters)
 * @param segments Number of segments for curve smoothness
 * @returns THREE.CatmullRomCurve3
 */
export function createParabolicCurve(
  start: LocalCoordinates,
  end: LocalCoordinates,
  apexHeight: number,
  segments: number = 50
): THREE.CatmullRomCurve3 {
  const startVec = new THREE.Vector3(start.x, 0, start.z);
  const endVec = new THREE.Vector3(end.x, 0, end.z);

  const points: THREE.Vector3[] = [];

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;

    // Linear interpolation for x and z
    const x = startVec.x + (endVec.x - startVec.x) * t;
    const z = startVec.z + (endVec.z - startVec.z) * t;

    // Parabolic arc for y (height)
    // Formula: y = -4h(t - 0.5)^2 + h (where h is apex height)
    // This creates a parabola with peak at t=0.5
    const y = -4 * apexHeight * Math.pow(t - 0.5, 2) + apexHeight;

    points.push(new THREE.Vector3(x, y, z));
  }

  return new THREE.CatmullRomCurve3(points);
}

/**
 * Calculate distance between two local coordinates
 */
export function calculateDistance(
  start: LocalCoordinates,
  end: LocalCoordinates
): number {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  return Math.sqrt(dx * dx + dz * dz);
}
